/** Year → 1-based month numbers with departures available, as returned by the API. */
export type AvailableDates = Record<string, number[]>;

export interface MonthYear {
  month: number;
  year: number;
}

export const MONTH_NAMES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

export function formatMonthYear(value: MonthYear | null): string {
  if (!value) return '';
  return `${MONTH_NAMES[value.month - 1] ?? value.month} ${value.year}`;
}

export function hasAvailableDates(availableDates: AvailableDates | null): boolean {
  return Boolean(availableDates && Object.keys(availableDates).length > 0);
}

export function isMonthAvailable(
  availableDates: AvailableDates | null,
  value: MonthYear | null,
): boolean {
  if (!availableDates || !value) return false;
  return (availableDates[String(value.year)] || []).includes(value.month);
}

/** Ascending (year, month) pairs, so the panel and the auto-pick agree on order. */
export function sortedMonthYears(availableDates: AvailableDates | null): MonthYear[] {
  if (!availableDates) return [];

  return Object.keys(availableDates)
    .map((year) => Number(year))
    .filter((year) => Number.isFinite(year))
    .sort((a, b) => a - b)
    .flatMap((year) =>
      [...(availableDates[String(year)] || [])]
        .sort((a, b) => a - b)
        .map((month) => ({ month, year })),
    );
}

/**
 * First available departure month at or after `now`, falling back to the
 * earliest one offered. Mirrors the legacy PackageFilterForm auto-selection so
 * an agency's hero lands on the same default month it always did.
 */
export function pickClosestAvailableMonth(
  availableDates: AvailableDates | null,
  now: Date = new Date(),
): MonthYear | null {
  const candidates = sortedMonthYears(availableDates);
  if (candidates.length === 0) return null;

  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const upcoming = candidates.find(
    ({ month, year }) => year > currentYear || (year === currentYear && month >= currentMonth),
  );

  return upcoming ?? candidates[0];
}
